import React from 'react';
import { Link } from 'react-router-dom';
import type { Article } from '../../../data/articles';

interface ArticleCardProps {
  article: Article;
  featured?: boolean;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article, featured = false }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <article className={`h-full flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group border border-gray-100 ${featured ? 'ring-2 ring-blue-500/20' : ''
      }`}>
      <Link to={`/blog/${article.slug}`} className="flex-1 flex flex-col">
        {/* Image Container */}
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
          />
          {featured && (
            <div className="absolute top-3 left-3 bg-blue-600/90 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
              Destaque
            </div>
          )}
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-xs font-medium shadow-sm">
            {article.category}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 flex flex-col">
          {/* Meta Info */}
          <div className="flex items-center gap-3 text-xs text-gray-400 mb-3 font-medium uppercase tracking-wide">
            <div className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              <span>{formatDate(article.date)}</span>
            </div>
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            <div className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              <span>{article.readTime} min leitura</span>
            </div>
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-blue-600 transition-colors">
            {article.title}
          </h3>

          <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4 flex-1">
            {article.excerpt}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {article.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 bg-gray-50 text-gray-500 text-xs rounded-md font-medium group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Footer Actions */}
          <div className="pt-4 border-t border-gray-100 flex items-center justify-between mt-auto">
            <span className="text-sm font-semibold text-blue-600 group-hover:translate-x-1 transition-transform flex items-center gap-1">
              Ler artigo <i className="ri-arrow-right-line"></i>
            </span>

            {article.relatedServiceId && (
              <object>
                <Link
                  to={`/servico/${article.relatedServiceId}`}
                  className="text-xs text-gray-400 hover:text-blue-600 transition-colors flex items-center gap-1 z-10 relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="ri-tools-line"></i>
                  <span>Serviço</span>
                </Link>
              </object>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
};

export default ArticleCard;

